import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { VscBell, VscCalendar, VscCheck, VscCommentDiscussion, VscHeart, VscInbox } from "react-icons/vsc";
import { useNotifications } from "../../context/NotificationContext";
import { getNotificationRoute, markAllAsRead, markAsRead } from "../../services/notificationsService";
import { supabase } from "../../supabase/supabaseClient";
import "./Notifications.css";

function Notifications() {
    const navigate = useNavigate();
    const {
        user,
        notifications,
        unreadCount,
        loading,
        refreshNotifications,
        refreshUnreadCount,
    } = useNotifications();

    const [postAlerts, setPostAlerts] = useState([]);
    const [postAlertsLoading, setPostAlertsLoading] = useState(false);
    const [messageRequests, setMessageRequests] = useState([]);
    const [messageRequestsLoading, setMessageRequestsLoading] = useState(false);

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const connectionPostNotifications = useMemo(() => {
        return (notifications || []).filter((notification) => {
            const type = (notification.type || "").toLowerCase();
            return type.includes("post") || type.includes("feed") || /post/i.test(notification.title || "");
        });
    }, [notifications]);

    const likeNotifications = useMemo(() => {
        return (notifications || []).filter((notification) => {
            const type = (notification.type || "").toLowerCase();
            return type.includes("like") || /liked/i.test(notification.title || "") || /liked/i.test(notification.body || "");
        });
    }, [notifications]);

    useEffect(() => {
        if (!user?.id) return;

        let isMounted = true;

        const loadPostSignals = async () => {
            setPostAlertsLoading(true);
            try {
                const { data: myPosts } = await supabase
                    .from("posts")
                    .select("id, body, created_at")
                    .eq("author_id", user.id)
                    .order("created_at", { ascending: false })
                    .limit(20);

                const postIds = (myPosts || []).map((post) => post.id);
                if (postIds.length === 0) {
                    if (isMounted) setPostAlerts([]);
                    return;
                }

                const { data: reactions } = await supabase
                    .from("post_reactions")
                    .select("post_id")
                    .in("post_id", postIds);

                const countsByPostId = (reactions || []).reduce((acc, row) => {
                    acc[row.post_id] = (acc[row.post_id] || 0) + 1;
                    return acc;
                }, {});

                const alerts = (myPosts || [])
                    .map((post) => ({
                        postId: post.id,
                        body: post.body || "",
                        createdAt: post.created_at,
                        likeCount: countsByPostId[post.id] || 0,
                    }))
                    .filter((item) => item.likeCount > 0)
                    .sort((a, b) => b.likeCount - a.likeCount)
                    .slice(0, 8);

                if (isMounted) setPostAlerts(alerts);
            } catch (error) {
                console.error("Error loading post notification signals:", error);
                if (isMounted) setPostAlerts([]);
            } finally {
                if (isMounted) setPostAlertsLoading(false);
            }
        };

        const loadMessageRequests = async () => {
            setMessageRequestsLoading(true);
            try {
                const { data: requests } = await supabase
                    .from("conversations")
                    .select("id, initiator_id, recipient_id, status, created_at")
                    .eq("recipient_id", user.id)
                    .eq("status", "pending")
                    .order("created_at", { ascending: false })
                    .limit(10);

                const requesterIds = Array.from(new Set((requests || []).map((item) => item.initiator_id).filter(Boolean)));

                let userNameById = {};
                if (requesterIds.length > 0) {
                    const { data: userProfiles } = await supabase
                        .from("user_profiles")
                        .select("user_id, first_name, last_name")
                        .in("user_id", requesterIds);

                    (userProfiles || []).forEach((profile) => {
                        const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                        if (fullName) userNameById[profile.user_id] = fullName;
                    });

                    const { data: playerProfiles } = await supabase
                        .from("player_profiles")
                        .select("user_id, firstname, lastname")
                        .in("user_id", requesterIds);

                    (playerProfiles || []).forEach((profile) => {
                        if (!userNameById[profile.user_id]) {
                            const fullName = `${profile.firstname || ""} ${profile.lastname || ""}`.trim();
                            if (fullName) userNameById[profile.user_id] = fullName;
                        }
                    });
                }

                const mapped = (requests || []).map((item) => ({
                    id: item.id,
                    requesterId: item.initiator_id,
                    requesterName: userNameById[item.initiator_id] || "User",
                    createdAt: item.created_at,
                }));

                if (isMounted) setMessageRequests(mapped);
            } catch (error) {
                console.error("Error loading message requests:", error);
                if (isMounted) setMessageRequests([]);
            } finally {
                if (isMounted) setMessageRequestsLoading(false);
            }
        };

        loadPostSignals();
        loadMessageRequests();

        return () => {
            isMounted = false;
        };
    }, [user]);

    const handleNotificationClick = async (notification) => {
        if (!notification.read_at) {
            await markAsRead(notification.id);
            await refreshUnreadCount();
            await refreshNotifications();
        }

        const route = getNotificationRoute(notification);
        navigate(route.path, { state: route.state });
    };

    const handleMarkAllRead = async () => {
        if (!user?.id) return;
        await markAllAsRead(user.id);
        await refreshUnreadCount();
        await refreshNotifications();
    };

    if (loading) {
        return (
            <main className="page notifications-page">
                <section className="notifications-card">
                    <p>Loading notifications...</p>
                </section>
            </main>
        );
    }

    return (
        <main className="page notifications-page">
            <div className="notifications-shell">
                <section className="notifications-card">
                    <div className="notifications-header">
                        <div>
                            <h1>Notifications</h1>
                            <p>Your updates across posts, likes, and message requests.</p>
                        </div>
                        <button
                            type="button"
                            className="notifications-mark-read"
                            onClick={handleMarkAllRead}
                            disabled={unreadCount === 0}
                        >
                            <VscCheck /> Mark all as read
                        </button>
                    </div>
                    <div className="notifications-pill-row">
                        <span className="notifications-pill"><VscBell /> {unreadCount} unread</span>
                        <span className="notifications-pill"><VscInbox /> {messageRequests.length} message requests</span>
                    </div>
                </section>

                <section className="notifications-card">
                    <div className="notifications-section-title">
                        <VscCalendar />
                        <h2>Opportunities</h2>
                    </div>
                    <p className="notifications-empty">No new opportunities yet. This section is coming soon.</p>
                </section>

                <section className="notifications-card">
                    <div className="notifications-section-title">
                        <VscBell />
                        <h2>New Posts</h2>
                    </div>
                    {connectionPostNotifications.length === 0 ? (
                        <p className="notifications-empty">No new post alerts yet.</p>
                    ) : (
                        <div className="notifications-list">
                            {connectionPostNotifications.slice(0, 12).map((notification) => (
                                <button
                                    key={notification.id}
                                    type="button"
                                    className={`notifications-item ${!notification.read_at ? "unread" : ""}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notifications-item-content">
                                        <p className="notifications-item-title">{notification.title || "New post"}</p>
                                        {notification.body && <p className="notifications-item-body">{notification.body}</p>}
                                    </div>
                                    <span className="notifications-item-time">{formatTime(notification.created_at)}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                <section className="notifications-card">
                    <div className="notifications-section-title">
                        <VscHeart />
                        <h2>Post Likes</h2>
                    </div>
                    {postAlertsLoading ? (
                        <p className="notifications-empty">Loading like activity...</p>
                    ) : postAlerts.length === 0 && likeNotifications.length === 0 ? (
                        <p className="notifications-empty">No likes on your posts yet.</p>
                    ) : (
                        <div className="notifications-list">
                            {postAlerts.map((alert) => (
                                <Link key={alert.postId} to="/home" className="notifications-link-item">
                                    <div className="notifications-item-content">
                                        <p className="notifications-item-title">Your post has {alert.likeCount} like{alert.likeCount === 1 ? "" : "s"}</p>
                                        <p className="notifications-item-body">{alert.body ? (alert.body.length > 110 ? `${alert.body.slice(0, 110)}...` : alert.body) : "Open Home to view the full post."}</p>
                                    </div>
                                    <span className="notifications-item-time">{formatTime(alert.createdAt)}</span>
                                </Link>
                            ))}
                            {likeNotifications.slice(0, 8).map((notification) => (
                                <button
                                    key={notification.id}
                                    type="button"
                                    className={`notifications-item ${!notification.read_at ? "unread" : ""}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notifications-item-content">
                                        <p className="notifications-item-title">{notification.title || "Someone liked your post"}</p>
                                        {notification.body && <p className="notifications-item-body">{notification.body}</p>}
                                    </div>
                                    <span className="notifications-item-time">{formatTime(notification.created_at)}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                <section className="notifications-card">
                    <div className="notifications-section-title">
                        <VscCommentDiscussion />
                        <h2>Message Requests</h2>
                    </div>
                    {messageRequestsLoading ? (
                        <p className="notifications-empty">Loading message requests...</p>
                    ) : messageRequests.length === 0 ? (
                        <p className="notifications-empty">No message requests right now.</p>
                    ) : (
                        <div className="notifications-list">
                            {messageRequests.map((request) => (
                                <Link
                                    key={request.id}
                                    to="/messages"
                                    state={{ conversationId: request.id }}
                                    className="notifications-link-item"
                                >
                                    <div className="notifications-item-content">
                                        <p className="notifications-item-title">{request.requesterName} sent you a message request</p>
                                        <p className="notifications-item-body">Tap to open the request in Messaging.</p>
                                    </div>
                                    <span className="notifications-item-time">{formatTime(request.createdAt)}</span>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </main>
    );
}

export default Notifications;
